/**
 * <bundle-save-drawer>
 *
 * Side-drawer host for the Bundle & Save widget. Extends SideDrawer
 * (defined in side-drawer.js) for open/close, overlay, focus trap.
 *
 * Adds two interactive controls above the lazy-loaded campaign tabs:
 *   1. Shape <select>     — re-filters the size chips
 *   2. Size chip radios   — selecting a chip updates the underlying
 *                           <bundle-save-widget>'s main variant id +
 *                           main price (used for discount preview and
 *                           cart-add). PDP variant picker is NOT touched.
 *
 * Click delegation for the open trigger is also set up here so that any
 * element with [data-bundle-drawer-open="<drawer-id>"] opens the drawer.
 */
if (!customElements.get('bundle-save-drawer')) {
  class BundleSaveDrawer extends SideDrawer {
    constructor() {
      super();
      this._wired = false;
    }

    connectedCallback() {
      // After a color-picker morph, a duplicate drawer with the same id can be
      // inserted inside <main>. Two elements sharing an id breaks the
      // getElementById-based opener and traps the drawer inside main's
      // stacking context. Dedupe + relocate to <body> so position:fixed
      // resolves against the viewport.
      if (this.id && this.parentNode !== document.body) {
        queueMicrotask(() => {
          if (!this.isConnected) return;
          const duplicates = document.querySelectorAll(
            `#${CSS.escape(this.id)}`
          );
          duplicates.forEach((el) => {
            if (el !== this) el.remove();
          });
          if (this.parentNode !== document.body) {
            document.body.appendChild(this);
          }
        });
      }

      if (this._wired) return;
      this._wired = true;

      this.variantPicker = this.querySelector('.bs-drawer__variant-picker');
      this.variantSummaryText = this.querySelector('[data-bs-variant-summary-text]');
      this.shapeSelect = this.querySelector('[data-bs-shape]');
      this.sizeChipsWrap = this.querySelector('[data-bs-sizes]');
      this.widget = this.querySelector('bundle-save-widget');
      this.tabsEl = this.querySelector('.bundle-tabs');
      this.footerCta = this.querySelector('[data-bs-drawer-cta]');
      this.footerHint = this.querySelector('[data-bs-drawer-hint]');
      this.statusRegion = this.querySelector('[data-bs-drawer-status]');
      this.footerSummary = this.querySelector('[data-bs-drawer-summary]');
      this._defaultCtaLabel = this.footerCta ? this.footerCta.textContent.trim() : 'Add Bundle to Cart';

      // Whenever shape changes, refilter the chip set and auto-select
      // the first available chip in the new shape (or keep current if
      // it still matches).
      if (this.shapeSelect) {
        this.shapeSelect.addEventListener('change', () => {
          this._applyShapeFilter();
          this._activateFirstAvailableInView();
        });
      }

      if (this.sizeChipsWrap) {
        this.sizeChipsWrap.addEventListener('click', (evt) => {
          const chip = evt.target.closest('[data-variant-id]');
          if (!chip) return;
          if (chip.dataset.available === 'false') return;
          this._selectChip(chip);
        });

        // ARIA Radio Pattern keyboard nav: roving tabindex + arrow/Home/End.
        this.sizeChipsWrap.addEventListener('keydown', (evt) => this._onChipKeydown(evt));
      }

      // Apply the initial shape filter so chips for other shapes are
      // hidden from the start.
      this._applyShapeFilter();

      // Mirror the widget's selection state into the footer CTA.
      if (this.widget) {
        this.widget.addEventListener(
          'bundle:selection-change',
          (evt) => this._onSelectionChange(evt.detail)
        );
        this.widget.addEventListener(
          'bundle:add-error',
          (evt) => this._onAddError(evt.detail)
        );
        this.widget.addEventListener(
          'bundle:add-success',
          () => this._setStatus('Bundle added to cart.')
        );
      }

      // Footer button delegates to whichever summary's confirm button is
      // currently visible inside the widget. The widget's existing click
      // handler then runs the cart-add flow as before.
      if (this.footerCta) {
        this.footerCta.addEventListener('click', () => this._handleFooterClick());
      }

      // Tab overflow indicator — toggle has-overflow-left/right classes
      // as the user scrolls horizontally so the mask-image fades signal
      // hidden tabs. Also recompute on window resize.
      if (this.tabsEl) {
        const sync = () => this._syncTabOverflow();
        this.tabsEl.addEventListener('scroll', sync, { passive: true });
        window.addEventListener('resize', sync);
        // Initial pass once panels have had a chance to render.
        requestAnimationFrame(sync);
      }
    }

    /**
     * Override SideDrawer.open() to re-sync from the PDP's currently
     * selected variant before the drawer becomes visible. This keeps the
     * drawer consistent if the user changed shape/size on the PDP between
     * opens. Read-only — never mutates the PDP picker or URL.
     *
     * Also picks an explicit focus target (the close button) so screen
     * readers announce a predictable, named element on open rather than
     * landing on the drawer container itself. The drawer's
     * aria-labelledby still ensures the title is announced.
     */
    open(opener, elementToFocus, callback) {
      this._syncFromPdp();
      this._setStatus('');
      const target = elementToFocus || this.querySelector('.bs-drawer__close');
      super.open(opener, target, callback);
    }

    /**
     * Override SideDrawer.close() to clear any in-progress bundle pick when
     * the drawer is dismissed. Mirrors the tab-switch reset in the widget
     * so reopening the drawer starts from a clean slate.
     */
    close(callback) {
      if (this.widget && typeof this.widget._resetAllPanelState === 'function') {
        this.widget._resetAllPanelState();
      }
      super.close(callback);
    }

    /* ── PDP → drawer sync ── */

    _syncFromPdp() {
      if (!this.sizeChipsWrap) return;

      const variantId = this._readPdpVariantId();
      if (!variantId) return;

      const targetChip = this.sizeChipsWrap.querySelector(
        `[data-variant-id="${CSS.escape(String(variantId))}"]`
      );
      if (!targetChip) return;

      // Snap the shape select to the target chip's shape, refilter, then
      // select the chip. _selectChip also notifies the widget — we call it
      // even when the chip is already visually `is-on`, because the widget
      // needs setMainVariant() to prime _mainPriceCents for savings math.
      if (this.shapeSelect && targetChip.dataset.shape) {
        const desiredShape = targetChip.dataset.shape;
        const hasOption = Array.from(this.shapeSelect.options).some(
          (o) => o.value === desiredShape
        );
        if (hasOption && this.shapeSelect.value !== desiredShape) {
          this.shapeSelect.value = desiredShape;
        }
      }
      this._applyShapeFilter();
      this._selectChip(targetChip);
    }

    /**
     * Resolves the PDP's currently selected variant id. Tries, in order:
     *   1. The product form's hidden id input (most reliable; updated by
     *      variant-picker.js on every option change).
     *   2. The URL ?variant= param (set when variant-picker has
     *      data-update-url="true").
     *   3. null (caller falls back to whatever the drawer last had).
     */
    _readPdpVariantId() {
      const productHandle = this.dataset.productHandle;
      const productId = this.dataset.productId;

      // Prefer a form scoped to this product (avoids picking up an
      // unrelated featured-product form on the page).
      const forms = document.querySelectorAll('form[action*="/cart/add"]');
      for (const form of forms) {
        const idInput = form.querySelector('input[name="id"]');
        if (!idInput || !idInput.value) continue;

        const formProductId = form.dataset.productId
          || form.querySelector('[name="product-id"]')?.value;
        if (productId && formProductId && String(formProductId) !== String(productId)) {
          continue;
        }
        if (productHandle && form.action && !form.action.includes(productHandle)
            && formProductId !== productId) {
          continue;
        }
        return idInput.value;
      }

      // Fallback: URL ?variant=
      const urlVariant = new URL(window.location.href).searchParams.get('variant');
      if (urlVariant) return urlVariant;

      return null;
    }

    /* ── Shape → size filtering ── */

    _selectedShape() {
      return this.shapeSelect ? this.shapeSelect.value : (this.dataset.initialShape || '');
    }

    _applyShapeFilter() {
      if (!this.sizeChipsWrap) return;
      const selectedShape = this._selectedShape();
      const chips = this.sizeChipsWrap.querySelectorAll('[data-variant-id]');
      // If we have no shape data at all, show everything.
      const filtering = !!selectedShape;
      for (const chip of chips) {
        const matches = !filtering || chip.dataset.shape === selectedShape;
        chip.hidden = !matches;
      }
    }

    _activateFirstAvailableInView() {
      if (!this.sizeChipsWrap) return;
      const visible = Array.from(this.sizeChipsWrap.querySelectorAll('[data-variant-id]'))
        .filter((c) => !c.hidden);
      // Prefer keeping the currently-selected chip if it's still visible.
      const current = visible.find((c) => c.classList.contains('is-on'));
      if (current) return;
      const firstAvailable = visible.find((c) => c.dataset.available !== 'false') || visible[0];
      if (firstAvailable) this._selectChip(firstAvailable);
    }

    /* ── Size chip selection ── */

    /**
     * Selects a size chip and updates ARIA state per the WAI radio pattern:
     * only one chip carries tabindex=0 at a time; the rest get -1. Pass
     * { focus: true } for keyboard-driven selection (arrow keys) so focus
     * follows the active radio.
     */
    _selectChip(chip, options) {
      if (!this.sizeChipsWrap) return;
      for (const c of this.sizeChipsWrap.querySelectorAll('[data-variant-id]')) {
        c.classList.remove('is-on');
        c.setAttribute('aria-checked', 'false');
        c.setAttribute('tabindex', '-1');
      }
      chip.classList.add('is-on');
      chip.setAttribute('aria-checked', 'true');
      chip.setAttribute('tabindex', '0');
      if (options && options.focus) chip.focus();

      const variantId = chip.dataset.variantId;
      const priceCents = parseInt(chip.dataset.price, 10) || 0;
      const sizeLabel = chip.dataset.size || '';
      const shapeLabel = chip.dataset.shape || '';
      const priceFormatted = chip.dataset.priceFormatted || this._formatMoney(priceCents);

      // Keep the collapsed <summary> label in sync so the user sees the
      // currently-picked variant at a glance without expanding.
      if (this.variantSummaryText) {
        const parts = [];
        if (shapeLabel) parts.push(shapeLabel);
        if (sizeLabel) parts.push(sizeLabel);
        const prefix = parts.length ? parts.join(' ') + ' — ' : '';
        this.variantSummaryText.textContent = prefix + priceFormatted;
      }

      if (this.widget && typeof this.widget.setMainVariant === 'function') {
        this.widget.setMainVariant(variantId, priceCents, sizeLabel);
      }
    }

    /**
     * ARIA Radio Pattern keyboard handler. Arrow keys move focus between
     * visible, available chips (sold-out chips are skipped per APG's
     * "skip disabled radios" guidance). Home/End jump to first/last.
     * Each move also selects the chip ("select follows focus").
     */
    _onChipKeydown(evt) {
      const navKeys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
      if (!navKeys.includes(evt.key)) return;
      const current = evt.target.closest('[data-variant-id]');
      if (!current) return;

      const navigable = Array.from(
        this.sizeChipsWrap.querySelectorAll('[data-variant-id]')
      ).filter((c) => !c.hidden && c.dataset.available !== 'false');
      if (navigable.length === 0) return;

      evt.preventDefault();

      let idx = navigable.indexOf(current);
      if (idx < 0) idx = 0;

      switch (evt.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          idx = (idx + 1) % navigable.length;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          idx = (idx - 1 + navigable.length) % navigable.length;
          break;
        case 'Home':
          idx = 0;
          break;
        case 'End':
          idx = navigable.length - 1;
          break;
      }

      const target = navigable[idx];
      if (target) this._selectChip(target, { focus: true });
    }

    /* ── Footer CTA ── */

    _onSelectionChange(detail) {
      if (!this.footerCta) return;

      if (!detail || !detail.selected) {
        this.footerCta.disabled = true;
        this.footerCta.classList.remove('is-ready');
        this.footerCta.textContent = this._defaultCtaLabel;
        if (this.footerHint) this.footerHint.hidden = false;
        this._clearFooterSummary();
        return;
      }

      this.footerCta.disabled = false;
      this.footerCta.classList.add('is-ready');
      const label = detail.savingsCents > 0
        ? `Add Bundle & Save ${detail.savingsFormatted}`
        : 'Add Bundle to Cart';
      this.footerCta.textContent = label;
      if (this.footerHint) this.footerHint.hidden = true;

      this._populateFooterSummary(detail);
    }

    /**
     * Populate the sticky footer summary from the currently-visible
     * inline campaign summary (which the widget keeps populated via
     * _handleCardSelect + setMainVariant). The inline summary is
     * display:none in drawer mode but still lives in the DOM as source
     * of truth. Falls back to values in the event detail for anything
     * the inline summary didn't provide.
     */
    _populateFooterSummary(detail) {
      if (!this.footerSummary || !this.widget) return;
      const inline = this.widget.querySelector('[data-bundle-summary]:not([hidden])');
      if (!inline) return;

      const mainTitle = inline.dataset.mainProductTitle || '';
      const mainLabelSrc = inline.querySelector('[data-bundle-main-row-label]');
      const mainOldSrc = inline.querySelector('[data-bundle-main-old-price]');
      const mainNewSrc = inline.querySelector('[data-bundle-main-new-price]');
      const itemNameSrc = inline.querySelector('[data-bundle-item-name]');
      const itemPriceSrc = inline.querySelector('[data-bundle-item-price]');
      const totalSrc = inline.querySelector('[data-bundle-total]');

      const mainLabelEl = this.footerSummary.querySelector('[data-bs-drawer-main-label]');
      const mainOldEl = this.footerSummary.querySelector('[data-bs-drawer-main-old]');
      const mainNewEl = this.footerSummary.querySelector('[data-bs-drawer-main-new]');
      const itemNameEl = this.footerSummary.querySelector('[data-bs-drawer-item-name]');
      const itemPriceEl = this.footerSummary.querySelector('[data-bs-drawer-item-price]');
      const totalEl = this.footerSummary.querySelector('[data-bs-drawer-total]');
      const saveEl = this.footerSummary.querySelector('[data-bs-drawer-save]');

      if (mainLabelEl) mainLabelEl.textContent = (mainLabelSrc ? mainLabelSrc.textContent.trim() : mainTitle) || mainTitle;
      if (mainOldEl && mainOldSrc) mainOldEl.textContent = mainOldSrc.textContent.trim();
      if (mainNewEl && mainNewSrc) mainNewEl.textContent = mainNewSrc.textContent.trim();
      if (itemNameEl && itemNameSrc) itemNameEl.textContent = itemNameSrc.textContent.trim();
      if (itemPriceEl && itemPriceSrc) itemPriceEl.textContent = itemPriceSrc.textContent.trim();
      if (totalEl) totalEl.textContent = (totalSrc && totalSrc.textContent.trim()) || detail.totalFormatted || '';
      if (saveEl) saveEl.textContent = detail.savingsFormatted || '';

      this.footerSummary.hidden = false;
    }

    _clearFooterSummary() {
      if (!this.footerSummary) return;
      this.footerSummary.hidden = true;
    }

    /**
     * Toggle has-overflow-left/right on the tabs container based on
     * scroll position. CSS uses these classes to drive an edge mask
     * fade, signalling that tabs exist offscreen.
     */
    _syncTabOverflow() {
      if (!this.tabsEl) return;
      const { scrollLeft, scrollWidth, clientWidth } = this.tabsEl;
      const epsilon = 2;
      this.tabsEl.classList.toggle('has-overflow-left', scrollLeft > epsilon);
      this.tabsEl.classList.toggle(
        'has-overflow-right',
        scrollWidth - (scrollLeft + clientWidth) > epsilon
      );
    }

    _handleFooterClick() {
      if (!this.footerCta || this.footerCta.disabled || !this.widget) return;
      const summary = this.widget.querySelector('[data-bundle-summary]:not([hidden])');
      const confirmBtn = summary && summary.querySelector('[data-bundle-confirm]');
      if (!confirmBtn) return;

      this._busyLabel = this.footerCta.textContent;
      this.footerCta.disabled = true;
      this.footerCta.textContent = 'Adding...';
      this._setStatus('Adding bundle to cart.');

      // Existing widget handler runs via the delegated click listener;
      // on success the host (this drawer) is closed by the widget.
      confirmBtn.click();
    }

    _onAddError(detail) {
      this._resetFooterAfterError();
      const msg = (detail && detail.message) || "Couldn't add bundle. Please try again.";
      this._setStatus(msg);
    }

    _resetFooterAfterError() {
      if (!this.footerCta) return;
      this.footerCta.disabled = false;
      if (this._busyLabel) {
        this.footerCta.textContent = this._busyLabel;
        this._busyLabel = null;
      }
    }

    /**
     * Writes to the visually-hidden [role=status] live region so screen
     * reader users get feedback on Adding / Added / Error transitions
     * without a visible toast.
     */
    _setStatus(message) {
      if (!this.statusRegion) return;
      // Force a text change even if the same message fires twice so ATs
      // re-announce.
      this.statusRegion.textContent = '';
      if (message) {
        // Next microtask — some ATs otherwise coalesce identical updates.
        Promise.resolve().then(() => {
          this.statusRegion.textContent = message;
        });
      }
    }

    _formatMoney(cents) {
      const fmt = window.theme && window.theme.settings && window.theme.settings.moneyFormat;
      if (window.Shopify && window.Shopify.formatMoney && fmt) {
        return window.Shopify.formatMoney(cents, fmt);
      }
      return '$' + (cents / 100).toFixed(2);
    }
  }

  customElements.define('bundle-save-drawer', BundleSaveDrawer);
}

/* Delegated opener — any [data-bundle-drawer-open="<id>"] opens the drawer. */
document.addEventListener('click', (evt) => {
  const trigger = evt.target.closest('[data-bundle-drawer-open]');
  if (!trigger) return;
  const drawer = document.getElementById(trigger.dataset.bundleDrawerOpen);
  if (drawer && typeof drawer.open === 'function') {
    evt.preventDefault();
    drawer.open(trigger);
  }
});
